// The grocery vocabulary, curated for trayflux (g61) from general knowledge of
// what a supermarket sells, and licensed with the rest of the repository
// (MIT). No retailer's or competitor's product database was copied. Where an
// item's aisle is debatable, the placement follows where Coop Switzerland files
// it on coop.ch. English only, with a few Swiss and South African words; brands
// are not recognised and fall to Other.
//
// Notation, expanded by lexicon.ts: aliases are separated by "|", written in
// the classifier's normal form (lower case, no accents, single spaces). A
// bracket at the end of a word lists its endings: "apple(s)" is apple and
// apples, "cherr(y/ies)" is cherry and cherries. Nothing is singularised
// automatically.
//
// role "attributive": a single word from this group names the item only when
// it is the item's last word, because it is often a modifier ("chicken tikka
// kit", "milk frother"). role "marker": a word that decides the aisle wherever
// it stands ("dog treats", "prescription cream"), unless a longer phrase
// covers it ("hot dogs").
//
// forms: where a qualifier moves a group. "frozen" moves any food to Frozen
// unless the group says otherwise.
//
// Changing anything here changes DICTIONARY_REVISION: a test pins the content
// to the revision, so an edit without a bump fails.

import type { CategoryId } from "./categories.js";

export const DICTIONARY_REVISION = 2;

export interface Forms {
  readonly frozen?: CategoryId;
  readonly dried?: CategoryId;
  readonly tinned?: CategoryId;
  readonly fresh?: CategoryId;
}

export interface Group {
  readonly category: CategoryId;
  readonly role?: "attributive" | "marker";
  readonly forms?: Forms;
  readonly aliases: string;
}

export const GROUPS: readonly Group[] = [
  // ---- Flowers & plants ----
  {
    category: "flowers", role: "attributive",
    aliases: "flower(s)",
  },
  {
    category: "flowers",
    aliases: "bouquet(s)|cactus|carnation(s)|christmas tree(s)|compost|cut flowers|daffodil(s)"
      + "|herb pot(s)|house plant(s)|houseplant(s)|hydrangea(s)|lil(y/ies)|orchid(s)|peon(y/ies)"
      + "|plant food|poinsettia(s)|pot plant(s)|potted plant(s)|potting soil|roses|succulent(s)"
      + "|sunflower(s)|tulip(s)|wreath(s)",
  },

  // ---- Fruit & vegetables ----
  {
    category: "produce", role: "attributive", forms: { dried: "snacks", tinned: "pantry" },
    aliases: "fruit(s)|apple(s)|pear(s)|banana(s)|orange(s)|lemon(s)|lime(s)|grapefruit(s)|grape(s)"
      + "|strawberr(y/ies)|raspberr(y/ies)|blueberr(y/ies)|blackberr(y/ies)|berr(y/ies)|cherr(y/ies)"
      + "|peach(es)|nectarine(s)|plum(s)|apricot(s)|mango(es)|mangos|pineapple(s)|melon(s)|watermelon(s)"
      + "|kiwi(s)|kiwifruit|pomegranate(s)|fig(s)|passion fruit|papaya(s)|clementine(s)|mandarin(s)"
      + "|satsuma(s)|tangerine(s)|rhubarb|coconut(s)|cranberr(y/ies)|redcurrant(s)|gooseberr(y/ies)"
      + "|lychee(s)|persimmon(s)|quince(s)"
      + "|blackcurrant(s)|cantaloupe(s)|elderberr(y/ies)|greengage(s)|guava(s)|honeydew"
      + "|kumquat(s)|mirabelle(s)|physalis|pomelo(s)",
  },
  {
    category: "produce", role: "attributive", forms: { tinned: "pantry" },
    aliases: "tomato(es)|potato(es)|onion(s)|garlic|ginger|corn|avocado(s)|mushroom(s)|pepper(s)|spinach",
  },
  {
    category: "produce", forms: { tinned: "pantry" },
    aliases: "cherry tomato(es)|plum tomato(es)|vine tomato(es)|sweet potato(es)|new potato(es)"
      + "|red onion(s)|white onion(s)|brown onion(s)|spring onion(s)|scallion(s)|shallot(s)"
      + "|garlic clove(s)|garlic bulb(s)|leek(s)|carrot(s)|ruebli|celery|celeriac|cucumber(s)"
      + "|courgette(s)|zucchini(s)|aubergine(s)|eggplant(s)|bell pepper(s)|red pepper(s)"
      + "|green pepper(s)|yellow pepper(s)|capsicum(s)|chilli|chillies|chili|chilis|chilies|chile(s)"
      + "|jalapeno(s)|broccoli|tenderstem|cauliflower(s)|cabbage(s)|red cabbage|savoy cabbage|kale"
      + "|brussels sprout(s)|sprout(s)|bean sprout(s)|lettuce(s)|iceberg|romaine|rocket|arugula"
      + "|salad|salad leaves|lambs lettuce|watercress|cress|chard|pak choi|bok choy|pea(s)"
      + "|sugar snap peas|sugar snaps|mange tout|green bean(s)|runner bean(s)|broad bean(s)"
      + "|fine bean(s)|asparagus|corn on the cob|sweetcorn|mealie(s)|beetroot(s)|beet(s)"
      + "|radish(es)|turnip(s)|parsnip(s)|swede|pumpkin(s)|butternut|butternut squash|squash"
      + "|fennel|fennel bulb(s)|artichoke(s)|okra|root ginger|fresh ginger|horseradish|kohlrabi"
      + "|alfalfa|baby leaf|cassava|cavolo nero|chanterelle(s)|chestnut(s)|chicory|endive(s)"
      + "|fresh turmeric|frisee|microgreens|plantain(s)|portobello|radicchio|romanesco|rucola"
      + "|salsify|shiitake|soup vegetables|spring greens|stir fry vegetables|turmeric root|yam(s)",
  },
  {
    category: "produce", forms: { dried: "pantry" },
    aliases: "parsley|coriander|cilantro|basil|mint|dill|chives|rosemary|thyme|sage|tarragon"
      + "|lemongrass|lemon grass|fresh herbs|herbs|curry leaves"
      + "|lime leaves",
  },

  // ---- Bakery ----
  {
    category: "bakery", role: "attributive",
    aliases: "bread(s)|toast|pastr(y/ies)|cake(s)|pie(s)|tortilla(s)",
  },
  {
    category: "bakery",
    aliases: "loaf|loaves|sourdough|baguette(s)|roll(s)|bread roll(s)|bun(s)|bagel(s)|croissant(s)"
      + "|pain au chocolat|brioche(s)|pitta(s)|pita(s)|naan(s)|naan bread|wrap(s)|flatbread(s)"
      + "|crumpet(s)|muffin(s)|english muffin(s)|scone(s)|tart(s)|doughnut(s)|donut(s)|danish"
      + "|puff pastry|shortcrust pastry|filo pastry|pizza base(s)|pizza dough|pastry base(s)"
      + "|flammkuchen|tarte flambee|cracker(s)|crispbread(s)|knackebrot|zopf|gipfeli|rye bread"
      + "|wholemeal bread|white bread|brown bread|ciabatta(s)|focaccia(s)|pretzel roll(s)"
      + "|hot cross bun(s)|hot dog roll(s)|hot dog bun(s)|burger bun(s)|garlic bread|cinnamon roll(s)"
      + "|banana bread|birthday cake|croutons"
      + "|apple strudel|breadstick(s)|brownie(s)|cheesecake(s)|crepe(s)|cupcake(s)|eclair(s)"
      + "|grissini|macaron(s)|madeleine(s)|meringue(s)|pancake(s)|panettone|quiche(s)|stollen"
      + "|strudel|waffle(s)|zwieback",
  },

  // ---- Meat & fish ----
  {
    category: "meat-fish", role: "attributive",
    aliases: "chicken|beef|pork|lamb|veal|turkey|duck|fish|salmon|tuna",
  },
  {
    category: "meat-fish",
    aliases: "chicken breast(s)|chicken thigh(s)|chicken wing(s)|chicken leg(s)|drumstick(s)"
      + "|whole chicken|thigh(s)|breast(s)|wing(s)|fillet(s)|filet(s)|steak(s)|mince|minced beef"
      + "|minced meat|beef mince|pork mince|lamb mince|ground beef|burger(s)|meatball(s)|chop(s)"
      + "|pork chop(s)|lamb chop(s)|rib(s)|spare ribs|sausage(s)|bratwurst|cervelat|boerewors"
      + "|wienerli|frankfurter(s)|hot dog(s)|bacon|lardon(s)|pancetta|guanciale|ham|salami"
      + "|chorizo|prosciutto|parma ham|cold cuts|cold meat|deli meat|biltong|bundnerfleisch"
      + "|schnitzel|liver|smoked salmon|salmon fillet(s)|tuna steak(s)|cod|haddock|trout"
      + "|sea bass|seabass|hake|tilapia|mackerel|prawn(s)|shrimp(s)|mussel(s)|clam(s)|squid"
      + "|calamari|scallop(s)|crab|lobster|seafood|anchov(y/ies)|venison|rabbit|goat"
      + "|black pudding|bresaola|brisket|chipolata(s)|coppa|cordon bleu|crayfish|entrecote|gyros"
      + "|herring|kebab(s)|kipper(s)|langoustine(s)|merguez|monkfish|mortadella|octopus|osso buco"
      + "|oxtail|oyster(s)|pastrami|pate|perch|pike|plaice|pork belly|pork loin|rollmops"
      + "|salsiccia|shank(s)|sirloin|sole|speck|swordfish|tenderloin|terrine",
  },

  // ---- Dairy, eggs & alternatives ----
  {
    category: "dairy", role: "attributive",
    aliases: "milk|cream|butter|cheese|egg(s)|yoghurt(s)|yogurt(s)",
  },
  {
    category: "dairy",
    aliases: "whole milk|full cream milk|skimmed milk|skim milk|semi skimmed milk|low fat milk"
      + "|oat milk|almond milk|soy milk|soya milk|rice milk|plant milk|coconut drink|oat drink"
      + "|double cream|single cream|whipping cream|sour cream|creme fraiche|clotted cream"
      + "|coffee cream|kaffeerahm|quark|cream cheese|cottage cheese|margarine|greek yoghurt"
      + "|greek yogurt|natural yoghurt|cheddar|gruyere|emmental|emmentaler|parmesan|parmigiano"
      + "|grana padano|pecorino|pecorino romano|mozzarella|burrata|feta|halloumi|brie|camembert"
      + "|gouda|edam|raclette|raclette cheese|fondue|fondue cheese|mascarpone|ricotta|goats cheese"
      + "|goat cheese|blue cheese|gorgonzola|roquefort|stilton|appenzeller|tete de moine|vacherin"
      + "|manchego|paneer|tofu|tempeh|hummus|houmous|kefir|buttermilk|chocolate milk|egg white(s)"
      + "|custard|creme brulee|milkshake(s)|vegan cheese"
      + "|ayran|chevre|comte|flan|fontina|fromage frais|ghee|iced coffee|labneh|lassi|mousse"
      + "|panna cotta|provolone|pudding(s)|reblochon|rice pudding|sbrinz|scamorza|skyr|taleggio"
      + "|tilsiter|tiramisu|tomme|yoghurt drink",
  },

  // ---- Ready meals & deli ----
  {
    category: "ready-meals",
    aliases: "coleslaw|couscous salad|curry|dim sum|dip(s)|dumpling(s)|egg salad|falafel"
      + "|fondue chinoise|guacamole|meal kit(s)|microwave meal(s)|pasta salad|poke bowl(s)"
      + "|potato salad|ready meal(s)|roast chicken|rosti|rotisserie chicken|salad bowl(s)"
      + "|samosa(s)|sandwich(es)|sushi|tapenade|tzatziki",
  },

  // ---- Pantry & cooking ----
  {
    category: "pantry", role: "attributive",
    aliases: "rice|sugar|salt|honey|vanilla|cinnamon|oat(s)|flour|oil|sauce(s)|soup(s)|jam(s)",
  },
  {
    category: "pantry", forms: { fresh: "produce" },
    aliases: "oregano|bay leaf|bay leaves|marjoram|dried herbs|mixed herbs|herbes de provence",
  },
  {
    category: "pantry", forms: { fresh: "ready-meals" },
    aliases: "pasta|gnocchi|ravioli|tortellini",
  },
  {
    category: "pantry",
    aliases: "spaghetti|penne|fusilli|farfalle|rigatoni|tagliatelle|linguine|fettuccine"
      + "|lasagne|lasagna|lasagne sheets|macaroni|orzo|noodle(s)"
      + "|egg noodle(s)|rice noodle(s)|ramen|basmati|basmati rice|jasmine rice|risotto rice"
      + "|arborio|brown rice|couscous|quinoa|bulgur|bulgur wheat|polenta|semolina|mealie meal"
      + "|maize meal|plain flour|self raising flour|bread flour|cornflour|cornstarch|yeast"
      + "|baking powder|baking soda|bicarbonate of soda|bicarb|cream of tartar|gelatine|gelatin"
      + "|caster sugar|icing sugar|brown sugar|demerara sugar|sweetener|maple syrup|golden syrup"
      + "|agave syrup|marmalade|peanut butter|almond butter|chocolate spread|lemon curd"
      + "|olive oil|sunflower oil|vegetable oil|rapeseed oil|canola oil|coconut oil|sesame oil"
      + "|vinegar|balsamic|balsamic vinegar|wine vinegar|cider vinegar|rice vinegar"
      + "|black pepper|white pepper|ground pepper|peppercorn(s)|salt and pepper|cayenne pepper"
      + "|paprika|cumin|turmeric|nutmeg|clove(s)|cardamom|chilli powder|chili powder|chilli flake(s)"
      + "|chili flake(s)|red pepper flakes|curry powder|garam masala|ground coriander|coriander seed(s)"
      + "|ground ginger|garlic powder|onion powder|mustard seed(s)|fennel seed(s)|star anise"
      + "|saffron|spice(s)|seasoning|stock|stock cube(s)|stock pot(s)|bouillon|gravy|gravy granules"
      + "|chicken stock|beef stock|vegetable stock|fish stock|soy sauce|fish sauce|hot sauce"
      + "|tomato sauce|pasta sauce|peri peri sauce|sriracha|worcestershire sauce|pesto|ketchup"
      + "|mayonnaise|mayo|mustard|relish|chutney|mango chutney|salsa|curry paste|tomato paste"
      + "|tomato puree|passata|chopped tomato(es)|tinned tomato(es)|canned tomato(es)|sun dried tomato(es)"
      + "|bean(s)|baked beans|chickpea(s)|lentil(s)|kidney bean(s)|black bean(s)|butter bean(s)"
      + "|cannellini beans|borlotti beans|sardine(s)|tinned tuna|canned tuna|olive(s)|caper(s)"
      + "|pickle(s)|gherkin(s)|cornichon(s)|artichoke hearts|coconut milk|coconut cream"
      + "|condensed milk|evaporated milk|milk powder|desiccated coconut|cocoa powder"
      + "|baking chocolate|cooking chocolate|chocolate chip(s)|vanilla extract|vanilla pod(s)"
      + "|vanilla sugar|ground almonds|flaked almonds|almond flakes|nut filling|sunflower seed(s)"
      + "|pumpkin seed(s)|chia seed(s)|sesame seed(s)|linseed(s)|flaxseed(s)|cereal(s)|cornflakes"
      + "|corn flakes|muesli|granola|porridge|porridge oats|rolled oats|breadcrumbs"
      + "|bread crumbs|panko|tahini|nori|miso|apple sauce|lemon juice|lime juice|salad dressing"
      + "|dressing|custard powder|food colouring|sprinkles"
      + "|aioli|bearnaise|bechamel|bread mix|brownie mix|cake mix|cashew butter|chick pea(s)"
      + "|corn starch|corned beef|dried mushroom(s)|fondant|fruit cocktail|gochujang|harissa"
      + "|hoisin|hollandaise|icing|jelly|marzipan|meal replacement|nut butter|oatmeal|pancake mix"
      + "|pickled beetroot|pickled onion(s)|pine nut(s)|potato starch|protein powder|rice paper"
      + "|risotto|sambal|sauerkraut|stuffing|taco shell(s)|wasabi|whey protein",
  },

  // ---- Hot drinks ----
  {
    category: "hot-drinks", role: "attributive",
    aliases: "coffee|tea|cocoa",
  },
  {
    category: "hot-drinks",
    aliases: "coffee bean(s)|ground coffee|instant coffee|espresso|decaf|coffee capsule(s)"
      + "|coffee pod(s)|green tea|black tea|herbal tea|fruit tea|peppermint tea"
      + "|mint tea|chamomile|camomile|rooibos|earl grey|english breakfast|chai|matcha"
      + "|hot chocolate|drinking chocolate|tea bag(s)"
      + "|cappuccino|coffee creamer|infusion(s)|malt drink|yerba mate",
  },

  // ---- Snacks & sweets ----
  {
    category: "snacks", role: "attributive",
    aliases: "chocolate|peanut(s)|almond(s)|nut(s)|popcorn",
  },
  {
    category: "snacks",
    aliases: "chips|crisps|potato chips|tortilla chips|nachos|pretzel(s)|mixed nuts|salted nuts"
      + "|cashew(s)|pistachio(s)|macadamia(s)|hazelnut(s)|walnut(s)|pecan(s)|brazil nut(s)"
      + "|trail mix|student mix|dried fruit|raisin(s)|sultana(s)|date(s)|prune(s)|milk chocolate"
      + "|dark chocolate|white chocolate|chocolate bar(s)|sweets|candy|candies|gummies|gummy bears"
      + "|jelly babies|wine gums|liquorice|licorice|marshmallow(s)|mints|chewing gum|gum"
      + "|biscuit(s)|cookie(s)|digestive(s)|shortbread|wafer(s)|cereal bar(s)|granola bar(s)"
      + "|protein bar(s)|rice cake(s)|easter egg(s)|chocolate egg(s)|chocolate biscuit(s)"
      + "|jerky|snack(s)|lollipop(s)|toffee(s)|fudge|praline(s)|truffle(s)"
      + "|caramel(s)|energy ball(s)|flips|gingerbread|halva|jelly bean(s)|lebkuchen|muesli bar(s)"
      + "|nougat|nut mix|rice cracker(s)|salt stick(s)|spekulatius|turkish delight",
  },

  // ---- Drinks ----
  {
    category: "drinks", role: "attributive",
    aliases: "water|juice(s)",
  },
  {
    category: "drinks",
    aliases: "sparkling water|still water|mineral water|soda water|fizzy water|orange juice"
      + "|apple juice|smoothie(s)|lemonade|cola|tonic|tonic water|ginger beer|ginger ale"
      + "|soft drink(s)|fizzy drink(s)|orange squash|cordial|sirup|iced tea|ice tea"
      + "|energy drink(s)|sports drink(s)|kombucha|beer(s)|lager(s)|ale|ipa|cider(s)|rivella"
      + "|coconut water|bitter lemon|cold brew|hard seltzer|mixer(s)|pilsner|protein shake(s)"
      + "|radler|seltzer|shandy|soda(s)|stout",
  },

  // ---- Wine & spirits ----
  {
    category: "wine-spirits", role: "attributive",
    aliases: "wine(s)",
  },
  {
    category: "wine-spirits",
    aliases: "red wine|white wine|rose wine|sparkling wine|prosecco|champagne|cava|gin|vodka|whisky"
      + "|whiskey|rum|tequila|brandy|cognac|vermouth|aperol|liqueur|absinthe|amaretto"
      + "|cabernet sauvignon|chardonnay|chasselas|chianti|fendant|gluhwein|grappa|kirsch"
      + "|limoncello|malbec|merlot|mulled wine|pinot noir|pinotage|port|rioja|sauvignon blanc"
      + "|schnapps|sherry|shiraz|rose",
  },

  // ---- Household ----
  {
    category: "household",
    aliases: "toilet paper|toilet roll(s)|loo roll(s)|kitchen roll(s)|kitchen paper|paper towel(s)"
      + "|tissue(s)|napkin(s)|serviette(s)|washing up liquid|dish soap|dishwashing liquid"
      + "|dishwasher tablet(s)|dishwasher tab(s)|dishwasher salt|rinse aid|laundry detergent"
      + "|detergent|washing powder|washing liquid|laundry liquid|fabric softener"
      + "|fabric conditioner|stain remover|bleach|bin bag(s)|bin liner(s)|rubbish bag(s)"
      + "|sponge(s)|scourer(s)|cloth(s)|cling film|foil|tin foil|aluminium foil|baking paper"
      + "|greaseproof paper|freezer bag(s)|sandwich bag(s)|light bulb(s)|batter(y/ies)"
      + "|candle(s)|tea light(s)|matches|lighter|cleaner|surface cleaner|glass cleaner"
      + "|bathroom cleaner|toilet cleaner|descaler|limescale remover|air freshener|coffee filter(s)"
      + "|tea towel(s)|rubber gloves|dustpan|mop|broom|plastic wrap|zip bags"
      + "|a4 paper|baking tray(s)|balloon(s)|boxers|bucket|cake case(s)|cake tin(s)|calendar(s)"
      + "|charcoal|charger(s)|charging cable(s)|cleaning wipes|clothes peg(s)|coat hanger(s)"
      + "|cocktail stick(s)|colouring pencil(s)|compost bag(s)|crayon(s)|disinfectant"
      + "|disposable cutlery|dryer sheet(s)|earphone(s)|envelope(s)|eraser(s)|extension cable(s)"
      + "|extension lead(s)|fee bag(s)|felt tip(s)|filter cartridge(s)|firelighter(s)|firewood"
      + "|flashlight(s)|fly spray|folder(s)|food container(s)|fuse(s)|gift bag(s)|gift wrap"
      + "|glove(s)|glue|glue stick(s)|greeting card(s)|headphone(s)|highlighter(s)"
      + "|ice cube tray(s)|ink cartridge(s)|insect spray|ironing water|kitchen towel(s)"
      + "|laundry bag(s)|laundry capsule(s)|laundry pod(s)|led bulb(s)|leggings|lunch box(es)"
      + "|marker pen(s)|memory card(s)|mouse trap(s)|muffin case(s)|notebook(s)|notepad(s)"
      + "|oven glove(s)|pajamas|paper bag(s)|paper clip(s)|paper cup(s)|paper plate(s)"
      + "|party hat(s)|pen(s)|pencil sharpener(s)|pencil(s)|phone charger(s)|plastic cup(s)"
      + "|plug adapter(s)|printer ink|printer paper|pyjamas|rain poncho|ruler(s)|scissors"
      + "|scrubbing brush|shoe polish|shoelaces|slipper(s)|sock(s)|stapler|staples|steel wool"
      + "|sticky note(s)|sticky tape|t shirt(s)|tablecloth(s)|tights|toilet brush|toothpick(s)"
      + "|torch(es)|travel adapter(s)|umbrella(s)|underwear|usb cable(s)|usb stick(s)"
      + "|vacuum bag(s)|water filter(s)|wooden skewer(s)|wrapping paper|plate(s)",
  },

  // ---- Pet ----
  {
    category: "pet", role: "marker",
    aliases: "dog(s)|cat(s)|puppy|puppies|kitten(s)|pet(s)|guinea pig(s)|hamster(s)",
  },
  {
    category: "pet",
    aliases: "pet food|cat food|dog food|cat litter|kitty litter|bird seed|birdseed|fish food"
      + "|dog treats|fat ball(s)|hay|rabbit food|wild bird food",
  },

  // ---- Personal care ----
  {
    category: "personal-care", role: "marker",
    aliases: "prescription|pharmacy|chemist",
  },
  {
    category: "personal-care",
    aliases: "toothpaste|toothbrush(es)|dental floss|floss|mouthwash|shampoo|conditioner"
      + "|hair conditioner|shower gel|body wash|soap|hand soap|hand wash|deodorant|deo"
      + "|antiperspirant|razor(s)|razor blade(s)|shaving foam|shaving cream|shaving gel"
      + "|sun cream|suncream|sunscreen|sun lotion|after sun|moisturiser|moisturizer|body lotion"
      + "|hand cream|face cream|lip balm|cotton pads|cotton buds|cotton wool|tampon(s)"
      + "|sanitary pads|sanitary towels|panty liners|plaster(s)|band aid(s)|bandage(s)"
      + "|paracetamol|ibuprofen|aspirin|painkiller(s)|vitamin(s)|cough syrup|cough sweets"
      + "|throat lozenges|medicine|antihistamine(s)|hair gel|hairspray|hair spray|nail polish"
      + "|nail polish remover|makeup remover|face wash|contact lens solution|hand sanitiser"
      + "|hand sanitizer|condom(s)|lotion|perfume|cologne|hair dye|dry shampoo"
      + "|aftershave|barrier cream|bath foam|bath salt(s)|beard oil|blusher|bobby pin(s)"
      + "|body milk|body oil|body scrub|bubble bath|cleansing milk|comb(s)|concealer"
      + "|contact lens(es)|day cream|denture cleaner|denture tablet(s)|earplug(s)|electrolyte(s)"
      + "|eye cream|eye drops|eyeliner|face mask(s)|first aid kit|fish oil capsule(s)|foot cream"
      + "|foundation|hair brush|hair clip(s)|hair mask|hair oil|hair tie(s)|hairbrush"
      + "|hot water bottle|incontinence pad(s)|insect repellent|interdental brush(es)|lip gloss"
      + "|lipstick|magnesium|mascara|massage oil|menstrual cup(s)|micellar water"
      + "|mosquito repellent|multivitamin(s)|nail clipper(s)|nail file(s)|nasal spray|night cream"
      + "|nose spray|omega|pregnancy test(s)|probiotic(s)|self tan|serum|shower cap|thermometer"
      + "|throat spray|tweezers|wet wipes|zinc|hay fever|hair mousse",
  },

  // ---- Baby & child ----
  {
    category: "baby",
    aliases: "napp(y/ies)|diaper(s)|baby wipes|baby food|baby formula|formula milk|baby milk"
      + "|baby shampoo|baby lotion|baby oil|baby powder|rusk(s)|teething gel|baby bottle(s)"
      + "|baby cereal|baby porridge|baby sunscreen|baby toothpaste|board game(s)"
      + "|colouring book(s)|dumm(y/ies)|follow on milk|fruit pouch(es)|growing up milk"
      + "|nappy cream|pacifier(s)|playdough|puzzle(s)|sippy cup(s)|bottle teat(s)"
      + "|teething ring(s)|toddler milk|toy(s)",
  },

  // ---- Frozen ----
  {
    category: "frozen", role: "attributive",
    aliases: "ice|pizza(s)",
  },
  {
    category: "frozen",
    aliases: "ice cream|ice lolly|ice lollies|popsicle(s)|sorbet|gelato|frozen yoghurt"
      + "|fish finger(s)|fish stick(s)|oven chips|french fries|fries|potato wedges|hash brown(s)"
      + "|ice cube(s)|chicken nugget(s)|nugget(s)|fish cake(s)|spring roll(s)|edamame"
      + "|frozen vegetables|frozen veg|frozen pizza|onion rings"
      + "|croquette(s)|frozen meal(s)|glace|gyoza|potato croquettes|veggie burger(s)",
  },

  // ---- Kiosk & tobacco ----
  {
    category: "kiosk",
    aliases: "cigar(s)|cigarette(s)|e cigarette(s)|gift card(s)|lottery ticket(s)|lotto|magazine(s)"
      + "|newspaper(s)|phone credit|prepaid card(s)|rolling paper(s)|scratch card(s)|tobacco"
      + "|vape(s)",
  },
];
